import { useEffect, useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { Check, Pause, Clock, AlertCircle, MoreHorizontal, Loader2, FastForward, SkipForward, CheckCircle2, Lightbulb, HelpCircle } from 'lucide-react';
import { StepHelpModal } from './StepHelpModal';
import { formatScheduledFor, statusLabel, reasonLabel } from '../../lib/journeyHelpers';
import type { JourneyStep, UserJourneyProgress } from '../../types/klanttraject';
import { useToast } from '../../contexts/ToastContext';

interface Props {
  userId: string;
  onPersonalMailFor: (stepKey: string) => void;
  refreshKey?: number;
}

interface Row extends UserJourneyProgress {
  step: JourneyStep;
}

export function JourneyTimeline({ userId, onPersonalMailFor, refreshKey }: Props) {
  const { showToast } = useToast();
  const location = useLocation();
  const handleidingPath = location.pathname.startsWith('/support') ? '/support/handleiding' : '/admin/handleiding';
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyStepKey, setBusyStepKey] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  const [helpStep, setHelpStep] = useState<JourneyStep | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const [steps, progress] = await Promise.all([
        supabase.from('journey_steps').select('*').order('step_order', { ascending: true }),
        supabase.from('user_journey_progress').select('*').eq('user_id', userId),
      ]);
      if (cancelled) return;
      const stepMap = new Map((steps.data ?? []).map((s: any) => [s.step_key, s as JourneyStep]));
      const merged: Row[] = (progress.data ?? [])
        .map((p: any) => ({ ...(p as UserJourneyProgress), step: stepMap.get(p.step_key)! }))
        .filter(r => r.step)
        .sort((a, b) => a.step.step_order - b.step.step_order);
      setRows(merged);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [userId, reload, refreshKey]);

  async function forceNow(row: Row) {
    setBusyStepKey(row.step_key);
    try {
      const { error } = await supabase.from('user_journey_progress')
        .update({ scheduled_for: new Date().toISOString() })
        .eq('id', row.id);
      if (error) throw error;
      const { data: { session } } = await supabase.auth.getSession();
      await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/journey-tick`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${session?.access_token ?? ''}` },
      });
      showToast('Stap getriggerd', 'success');
      setReload(r => r + 1);
    } catch (e: any) {
      showToast('Fout', 'error', e.message);
    } finally { setBusyStepKey(null); }
  }

  async function skipStep(row: Row) {
    setBusyStepKey(row.step_key);
    try {
      const { error } = await supabase.from('user_journey_progress')
        .update({ status: 'skipped', completed_at: new Date().toISOString(), completed_reason: 'manual_support' })
        .eq('id', row.id);
      if (error) throw error;
      const { error: rpcErr } = await supabase.rpc('schedule_next_journey_step', { p_user_id: userId, p_current_step: row.step_key });
      if (rpcErr) throw rpcErr;
      showToast('Stap overgeslagen', 'success');
      setReload(r => r + 1);
    } catch (e: any) {
      showToast('Fout', 'error', e.message);
    } finally { setBusyStepKey(null); }
  }

  async function markManualOk(row: Row) {
    setBusyStepKey(row.step_key);
    try {
      const { error } = await supabase.from('user_journey_progress')
        .update({ status: 'completed', completed_at: new Date().toISOString(), completed_reason: 'manual_support' })
        .eq('id', row.id);
      if (error) throw error;
      const { error: rpcErr } = await supabase.rpc('schedule_next_journey_step', { p_user_id: userId, p_current_step: row.step_key });
      if (rpcErr) throw rpcErr;
      showToast('Stap goedgekeurd', 'success');
      setReload(r => r + 1);
    } catch (e: any) {
      showToast('Fout', 'error', e.message);
    } finally { setBusyStepKey(null); }
  }

  async function retryFailed(row: Row) {
    setBusyStepKey(row.step_key);
    try {
      const { error } = await supabase.from('user_journey_progress')
        .update({ status: 'scheduled', scheduled_for: new Date().toISOString(), last_error: null })
        .eq('id', row.id);
      if (error) throw error;
      const { data: { session } } = await supabase.auth.getSession();
      await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/journey-tick`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${session?.access_token ?? ''}` },
      });
      showToast('Opnieuw gepland', 'success');
      setReload(r => r + 1);
    } catch (e: any) {
      showToast('Fout', 'error', e.message);
    } finally { setBusyStepKey(null); }
  }

  if (loading) return <div className="bg-white rounded-lg border border-slate-200 p-4 flex items-center gap-2 text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Tijdlijn laden...</div>;
  if (rows.length === 0) return <div className="bg-white rounded-lg border border-slate-200 p-4 text-slate-500 text-sm">Nog geen klanttraject voor deze user. (Wizard nog niet voltooid?)</div>;

  return (
    <>
    <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-700">Klanttraject - tijdlijn</h3>
        <div className="flex items-center gap-3">
          <RouterLink
            to={`${handleidingPath}#klanttraject-overzicht`}
            className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 transition-colors"
            title="Open de handleiding voor het klanttraject"
          >
            <HelpCircle className="w-3.5 h-3.5" />
            Handleiding
          </RouterLink>
          <span className="text-xs text-slate-500">{rows.filter(r => r.status === 'completed' || r.status === 'skipped').length} / {rows.length} voltooid</span>
        </div>
      </div>
      <ul className="divide-y divide-slate-100">
        {rows.map(row => (
          <TimelineRow key={row.id} row={row} busy={busyStepKey === row.step_key}
            onForce={() => forceNow(row)}
            onSkip={() => skipStep(row)}
            onManualOk={() => markManualOk(row)}
            onRetry={() => retryFailed(row)}
            onPersonalMail={() => onPersonalMailFor(row.step_key)}
            onHelp={() => setHelpStep(row.step)}
          />
        ))}
      </ul>
    </div>
    {helpStep && <StepHelpModal stepKey={helpStep.step_key} title={helpStep.title} description={helpStep.description} onClose={() => setHelpStep(null)} />}
    </>
  );
}

interface TimelineRowProps {
  row: Row;
  busy: boolean;
  onForce: () => void;
  onSkip: () => void;
  onManualOk: () => void;
  onRetry: () => void;
  onPersonalMail: () => void;
  onHelp: () => void;
}

function TimelineRow({ row, busy, onForce, onSkip, onManualOk, onRetry, onPersonalMail, onHelp }: TimelineRowProps) {
  const Icon = (() => {
    if (row.step.type === 'nudge_mail' && row.status === 'scheduled') return Lightbulb;
    switch (row.status) {
      case 'completed': return Check;
      case 'skipped': return SkipForward;
      case 'pending_manual': return Pause;
      case 'scheduled': return Clock;
      case 'failed': return AlertCircle;
      case 'paused': return Pause;
      default: return MoreHorizontal;
    }
  })();
  const iconColor = (() => {
    switch (row.status) {
      case 'completed': return 'text-emerald-500';
      case 'skipped': return 'text-slate-400';
      case 'pending_manual': return 'text-amber-500';
      case 'scheduled': return 'text-blue-500';
      case 'failed': return 'text-red-500';
      default: return 'text-slate-300';
    }
  })();

  return (
    <li className={`px-4 py-3 flex items-start gap-3 ${row.step.type === 'nudge_mail' ? 'bg-amber-50/30' : ''}`}>
      <Icon className={`w-5 h-5 mt-0.5 flex-shrink-0 ${iconColor}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="text-xs text-slate-500">#{row.step.step_order}</span>
          <span className="text-sm font-medium text-slate-900">{row.step.title}</span>
          <span className="text-xs text-slate-400">({row.step.type})</span>
          <button onClick={onHelp} title="Uitleg" className="text-slate-300 hover:text-teal-600"><HelpCircle className="w-3.5 h-3.5" /></button>
          {row.step.type === 'nudge_mail' && <span className="text-[10px] text-amber-600 italic">voorwaardelijk</span>}
        </div>
        <div className="text-xs text-slate-600 mt-0.5 flex items-center flex-wrap gap-x-0">
          {statusLabel(row.status)}
          {row.completed_reason && ` · ${reasonLabel(row.completed_reason)}`}
          {row.status === 'scheduled' && row.scheduled_for && ` · ${formatScheduledFor(row.scheduled_for)}`}
          {row.status === 'completed' && row.completed_at && ` · ${new Date(row.completed_at).toLocaleString('nl-NL', { dateStyle: 'short', timeStyle: 'short' })}`}
          {row.last_error && <span className="text-red-600"> · {row.last_error.slice(0, 80)}</span>}
        </div>
      </div>
      <div className="flex gap-1.5 flex-shrink-0">
        {row.status === 'scheduled' && (
          <>
            <ActionBtn onClick={onForce} busy={busy}><FastForward className="w-3 h-3" /> Forceer</ActionBtn>
            <ActionBtn onClick={onSkip} busy={busy}><SkipForward className="w-3 h-3" /> Skip</ActionBtn>
          </>
        )}
        {row.status === 'pending_manual' && row.step.type === 'manual_review' && (
          <>
            <ActionBtn onClick={onSkip} busy={busy}><SkipForward className="w-3 h-3" /> Overslaan</ActionBtn>
            <ActionBtn onClick={onManualOk} busy={busy} primary><CheckCircle2 className="w-3 h-3" /> Markeer OK</ActionBtn>
          </>
        )}
        {row.status === 'pending_manual' && row.step.type === 'personal_mail_placeholder' && (
          <>
            <ActionBtn onClick={onSkip} busy={busy}><SkipForward className="w-3 h-3" /> Overslaan</ActionBtn>
            <ActionBtn onClick={onPersonalMail} busy={busy} primary>Stuur mail</ActionBtn>
          </>
        )}
        {row.status === 'failed' && <ActionBtn onClick={onRetry} busy={busy}>Opnieuw</ActionBtn>}
      </div>
    </li>
  );
}

interface ActionBtnProps {
  onClick: () => void;
  busy: boolean;
  primary?: boolean;
  children: React.ReactNode;
}

function ActionBtn({ onClick, busy, primary, children }: ActionBtnProps) {
  const cls = primary ? 'bg-teal-600 hover:bg-teal-700 text-white' : 'bg-slate-100 hover:bg-slate-200 text-slate-700';
  return (
    <button onClick={onClick} disabled={busy} className={`px-2 py-1 text-xs rounded ${cls} flex items-center gap-1 disabled:opacity-50`}>
      {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : children}
    </button>
  );
}
